import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';

const CATEGORIES = [
  { val:'MACHINE_FUEL',   label:'Machine Fuel',                 icon:'⛽' },
  { val:'DELIVERY',       label:'Delivery Expenses',            icon:'🚛' },
  { val:'PURCHASE',       label:'Purchase / Materials',         icon:'🛒' },
  { val:'SALARY',         label:'Salary / Wages',               icon:'💰' },
  { val:'EMPLOYEE',       label:'Employee Expenses',            icon:'👷' },
  { val:'FOOD',           label:'Food Expenses',                icon:'🍱' },
  { val:'TRAVEL',         label:'Travel Expenses',              icon:'✈️' },
  { val:'ELECTRICITY',    label:'Electricity Bill',             icon:'⚡' },
  { val:'GENERATOR_FUEL', label:'Generator Fuel',               icon:'🔋' },
  { val:'MAINTENANCE',    label:'Machine Maintenance / Repair', icon:'🔧' },
  { val:'RENT',           label:'Rent',                         icon:'🏢' },
  { val:'COMMUNICATION',  label:'Communication',                icon:'📱' },
  { val:'OFFICE',         label:'Office Expenses',              icon:'📋' },
  { val:'VEHICLE_MAINT',  label:'Vehicle Maintenance',          icon:'🚗' },
  { val:'MEDICAL_SAFETY', label:'Medical / Safety',             icon:'💊' },
  { val:'OTHER',          label:'Other Expenses',               icon:'📌' },
];
const PAYMENT_MODES = ['CASH','BANK','CHEQUE','UPI'];
const inr = v => `₹${Number(v||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;
const today = new Date().toISOString().split('T')[0];

const Field = ({ label, required, children, full }) => (
  <div className="form-group" style={full?{gridColumn:'1/-1'}:{}}>
    <label className="form-label">{label}{required&&<span style={{ color:'var(--red)',marginLeft:3 }}>*</span>}</label>
    {children}
  </div>
);
const ST = ({ children }) => (
  <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>{children}</div>
);

const EMPTY_FORM = { category:'', date:today, amount:'', payment_mode:'CASH', reference_number:'', vendor_name:'', notes:'' };

export default function ExpensePage() {
  const [expenses, setExpenses]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [msg, setMsg]             = useState('');
  const [filter, setFilter]       = useState({ category:'', from:'', to:'' });
  const [viewExpense, setViewExpense] = useState(null);

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const load = useCallback(() => {
    setLoading(true);
    let url = '/expenses/?ordering=-date';
    if(filter.category) url += `&category=${filter.category}`;
    if(filter.from)     url += `&from_date=${filter.from}`;
    if(filter.to)       url += `&to_date=${filter.to}`;
    api.get(url).then(r=>setExpenses(r.data.results||[])).finally(()=>setLoading(false));
  },[filter]);

  useEffect(()=>{ load(); },[load]);

  const handleSave = async () => {
    if(!form.category) { setError('Please select a category.'); return; }
    if(!form.amount)   { setError('Please enter amount.'); return; }
    if(!form.notes)    { setError('Notes are mandatory.'); return; }
    setSaving(true); setError('');
    try {
      if(form.id) {
        await api.patch(`/expenses/${form.id}/`, form);
      } else {
        await api.post('/expenses/', form);
      }
      setMsg('Expense saved!'); setTimeout(()=>setMsg(''),3000);
      setForm(EMPTY_FORM); setShowForm(false); load();
    } catch(e) { setError(e.response?.data?.errors?.notes?.[0]||'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, num) => {
    if(!window.confirm(`Delete expense ${num}? This cannot be undone.`)) return;
    try { await api.delete(`/expenses/${id}/`); load(); }
    catch { setError('Delete failed.'); }
  };

  // Category totals
  const totalExpenses = expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const catTotals = {};
  expenses.forEach(e=>{ catTotals[e.category]=(catTotals[e.category]||0)+parseFloat(e.amount||0); });

  return (
    <Layout title="Expenses" subtitle="Track all business expenses"
      actions={<button className="btn btn-primary" onClick={()=>{ setShowForm(true); setForm(EMPTY_FORM); setError(''); }}>+ Add Expense</button>}
    >
      {msg&&<div className="success-box" style={{ marginBottom:16 }}>{msg}</div>}

      {/* KPI */}
      <div className="kpi-grid" style={{ marginBottom:16 }}>
        <div className="kpi-card red"><div className="kpi-value">{inr(totalExpenses)}</div><div className="kpi-label">Total Expenses</div></div>
        <div className="kpi-card brand"><div className="kpi-value">{expenses.length}</div><div className="kpi-label">Total Records</div></div>
        <div className="kpi-card amber">
          <div className="kpi-value">{inr(Object.entries(catTotals).sort((a,b)=>b[1]-a[1])[0]?.[1]||0)}</div>
          <div className="kpi-label">{CATEGORIES.find(c=>c.val===Object.entries(catTotals).sort((a,b)=>b[1]-a[1])[0]?.[0])?.label||'Top Expense'}</div>
        </div>
        <div className="kpi-card blue">
          <div className="kpi-value">{expenses.filter(e=>e.payment_mode==='CASH').length}</div>
          <div className="kpi-label">Cash Payments</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:12, alignItems:'end' }}>
          <div className="form-group" style={{ flex:'1 1 160px' }}>
            <label className="form-label">Category</label>
            <select className="form-input" value={filter.category} onChange={e=>setFilter(f=>({...f,category:e.target.value}))}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c=><option key={c.val} value={c.val}>{c.icon} {c.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex:'1 1 140px' }}>
            <label className="form-label">From Date</label>
            <input className="form-input" type="date" value={filter.from} onChange={e=>setFilter(f=>({...f,from:e.target.value}))}/>
          </div>
          <div className="form-group" style={{ flex:'1 1 140px' }}>
            <label className="form-label">To Date</label>
            <input className="form-input" type="date" value={filter.to} onChange={e=>setFilter(f=>({...f,to:e.target.value}))}/>
          </div>
          <div className="form-group" style={{ flex:'0 0 auto' }}>
            <label className="form-label" style={{ opacity:0 }}>x</label>
            <button className="btn btn-secondary" onClick={()=>setFilter({category:'',from:'',to:''})}>Clear</button>
          </div>
        </div>
      </div>

      {/* Add Expense Form Modal */}
      {showForm&&(
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'var(--white)', borderRadius:'var(--radius)', padding:24, width:600, maxWidth:'95vw', maxHeight:'92vh', overflowY:'auto' }}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:16, color:'var(--text-primary)' }}>Add Expense</div>
            {error&&<div className="error-box" style={{ marginBottom:14 }}>{error}</div>}

            {/* Category Selection */}
            <div style={{ marginBottom:16 }}>
              <label className="form-label">Category <span style={{ color:'var(--red)' }}>*</span></label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))', gap:8, marginTop:8 }}>
                {CATEGORIES.map(c=>(
                  <div key={c.val} onClick={()=>setF('category',c.val)} style={{ padding:'8px 10px', borderRadius:'var(--radius-sm)', cursor:'pointer', border:`2px solid ${form.category===c.val?'var(--brand)':'var(--border)'}`, background:form.category===c.val?'var(--brand-dim)':'var(--white)', fontSize:12, fontWeight:form.category===c.val?700:400, color:form.category===c.val?'var(--brand)':'var(--text-secondary)', display:'flex', alignItems:'center', gap:6 }}>
                    <span>{c.icon}</span><span>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-grid">
              <Field label="Date *">
                <input className="form-input" type="date" value={form.date} onChange={e=>setF('date',e.target.value)} max={today}/>
              </Field>
              <Field label="Amount (₹) *">
                <input className="form-input" type="number" step="0.01" value={form.amount} onChange={e=>setF('amount',e.target.value)} placeholder="0.00"/>
              </Field>
              <Field label="Payment Mode *">
                <select className="form-input" value={form.payment_mode} onChange={e=>setF('payment_mode',e.target.value)}>
                  {PAYMENT_MODES.map(m=><option key={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Vendor / Paid To">
                <input className="form-input" value={form.vendor_name} onChange={e=>setF('vendor_name',e.target.value)} placeholder="Vendor or payee name"/>
              </Field>
              {(form.payment_mode==='BANK'||form.payment_mode==='CHEQUE'||form.payment_mode==='UPI')&&(
                <Field label="Reference Number" full>
                  <input className="form-input" value={form.reference_number} onChange={e=>setF('reference_number',e.target.value)} placeholder={form.payment_mode==='CHEQUE'?'Cheque number':form.payment_mode==='BANK'?'Transaction ID':'UPI reference'}/>
                </Field>
              )}
              <Field label="Notes (Mandatory) *" full>
                <textarea className="form-input" rows={3} value={form.notes} onChange={e=>setF('notes',e.target.value)} placeholder="Describe the expense in detail — what was purchased, why, for which machine/operation etc."/>
              </Field>
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
              <button className="btn btn-secondary" onClick={()=>{ setShowForm(false); setError(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving...':'Save Expense'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Expense List */}
      <div className="card">
        <ST>Expense Records</ST>
        {loading ? <div className="loading"><div className="spinner"/>Loading...</div> :
        expenses.length===0 ? <div className="empty-state"><p>No expenses found.</p></div> : (
          <div style={{ overflowX:'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Exp #</th>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Vendor</th>
                  <th>Payment</th>
                  <th>Notes</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e,i)=>{
                  const cat = CATEGORIES.find(c=>c.val===e.category);
                  return (
                    <tr key={i}>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'var(--brand)', fontWeight:600 }}>{e.expense_number}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, whiteSpace:'nowrap' }}>{e.date}</td>
                      <td>
                        <span style={{ fontSize:12, fontWeight:500 }}>{cat?.icon} {cat?.label||e.category}</span>
                      </td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{e.vendor_name||'—'}</td>
                      <td><span className="badge badge-blue">{e.payment_mode}</span></td>
                      <td style={{ fontSize:12, color:'var(--text-muted)', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={e.notes}>{e.notes}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontWeight:700, color:'var(--red)', whiteSpace:'nowrap' }}>{inr(e.amount)}</td>
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          <button className="btn btn-sm btn-secondary" onClick={()=>setViewExpense(e)}>View</button>
                          <button className="btn btn-sm btn-secondary" onClick={()=>{ setForm({...e}); setShowForm(true); setError(''); }}>Edit</button>
                          <button className="btn btn-sm btn-danger" onClick={()=>handleDelete(e.id,e.expense_number)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background:'var(--bg-primary)', borderTop:'2px solid var(--border)' }}>
                  <td colSpan={6} style={{ padding:'10px 16px', fontWeight:700, fontSize:13 }}>Total</td>
                  <td style={{ fontFamily:'DM Mono,monospace', fontWeight:700, color:'var(--red)', fontSize:15, padding:'10px 16px' }}>{inr(totalExpenses)}</td>
                  <td/>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      {viewExpense && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'var(--white)',borderRadius:'var(--radius)',padding:24,width:480,maxWidth:'90vw'}}>
            <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Expense Details — {viewExpense.expense_number}</div>
            {[
              ['Date', viewExpense.date],
              ['Category', CATEGORIES.find(c=>c.val===viewExpense.category)?.label||viewExpense.category],
              ['Amount', inr(viewExpense.amount)],
              ['Payment Mode', viewExpense.payment_mode],
              ['Vendor', viewExpense.vendor_name||'—'],
              ['Reference', viewExpense.reference_number||'—'],
              ['Notes', viewExpense.notes],
            ].map(([label,val])=>(
            <div key={label} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border-light)'}}>
              <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:700,textTransform:'uppercase',minWidth:140}}>{label}</span>
              <span style={{fontSize:13,color:'var(--text-primary)',textAlign:'right'}}>{val}</span>
            </div>
          ))}
          <div style={{display:'flex',justifyContent:'flex-end',marginTop:16,gap:10}}>
            <button className="btn btn-secondary" onClick={()=>setViewExpense(null)}>Close</button>
            <button className="btn btn-primary" onClick={()=>{ setForm({...viewExpense}); setViewExpense(null); setShowForm(true); }}>Edit</button>
          </div>
        </div>
      </div>
    )}
  </Layout>
  );
}
